import { URL_GET_EXPORT_FORMS } from '@utils/constants/urls'
import { RestParams, restWithToken } from '@utils/rest'
import moment from 'moment'
import { useState } from 'react'
import { useForm } from '../form/form.hook'

type ReportStatus = 'idle' | 'loading' | 'success' | 'error'

export interface ReportOptions {
  showCover: boolean
  pictureSize: 'small' | 'medium' | 'large'
  ordered: boolean
}

type UseReportObservationsPdfHook = {
  downloadPdf: (formId: string) => Promise<void>
  status: ReportStatus
  resetStatus: () => void
}

export const useReportFormsPdf = (formId: string): UseReportObservationsPdfHook => {
  const [status, setStatus] = useState<ReportStatus>('idle')
  const { data } = useForm(formId)

  const downloadPdf = async (formId: string): Promise<void> => {
    setStatus('loading')

    try {
      const apiUrl = `${URL_GET_EXPORT_FORMS}/${formId}`

      const params: RestParams = {
        url: apiUrl,
        method: 'GET',
        headers: {
          Accept: 'application/pdf',
          contentType: 'application/pdf',
        },
        responseType: 'blob',
      }

      const response = await restWithToken(params)

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`)
      }

      const contentType = response.headers.get('content-type')
      if (contentType && !contentType.includes('application/pdf')) {
        throw new Error('The file received is not a PDF')
      }

      const timestamp = moment().format('YYYY-MM-DD_HH-mm')
      const fileName = `${data?.form?.name.replace(' ', '-')}_${timestamp}.pdf`

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = fileName || 'report.pdf'
      link.style.display = 'none'
      document.body.appendChild(link)

      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      setStatus('success')
    } catch (error) {
      console.error('Erreur lors du téléchargement du PDF:', error)
      setStatus('error')
      throw error // Permet de gérer l'erreur au niveau du composant
    }
  }

  const resetStatus = () => setStatus('idle')

  return {
    downloadPdf,
    status,
    resetStatus,
  }
}
